import { TriangleAlert } from 'lucide-react'

import { formatDate } from '@/lib/format'

import { ReviewBlockCard } from './ReviewBlockCard'
import { WinnerCategoriesBlock } from './WinnerCategoriesBlock'
import { WinnerCustomerBlock } from './WinnerCustomerBlock'
import { WinnerProjectBlock } from './WinnerProjectBlock'
import { WinnerQuoteBlock } from './WinnerQuoteBlock'
import { WinnerSupplyBlock } from './WinnerSupplyBlock'
import { WinnerReviewSummary, type SummaryRow } from './WinnerReviewSummary'
import type { WinnerImportCustomerDecision, WinnerImportPreview, WinnerImportProjectDecision } from './winner-import.api'
import { formatEuro, type ReviewBlock } from './winner-import-progress'
import { categoriesSummary, isChoiceComplete, type CategoryChoice } from './winner-categories'
import { isSupplyChoiceComplete, supplySummary, type SupplyChoice } from './winner-supply'

type Props = {
  preview: WinnerImportPreview
  blocks: ReviewBlock[]
  confirmed: ReadonlySet<ReviewBlock>
  openBlock: ReviewBlock | null
  customer: WinnerImportCustomerDecision
  project: WinnerImportProjectDecision
  categoryChoices: Record<string, CategoryChoice>
  supplyChoices: Record<string, SupplyChoice>
  busy: boolean
  onCustomerChange: (decision: WinnerImportCustomerDecision) => void
  onProjectChange: (decision: WinnerImportProjectDecision) => void
  onCategoryChange: (key: string, choice: CategoryChoice) => void
  onSupplyChange: (key: string, choice: SupplyChoice) => void
  onConfirm: (block: ReviewBlock) => void
  onEdit: (block: ReviewBlock) => void
  onCreate: () => void
}

export function WinnerReview(props: Props) {
  const { preview, blocks, confirmed, openBlock, customer, project, categoryChoices, supplyChoices, busy } = props
  const quote = preview.quote!
  const entry = preview.entryProject
  const customerLabel = customer.mode === 'existing' ? `${customer.name} (client existant)` : `${customer.name || '—'} (nouveau client)`
  const projectLabel = project.mode === 'existing' ? 'Chantier existant choisi' : `${project.name || '—'} (nouveau chantier)`

  const groups = preview.categories?.groups ?? []
  const categoriesLabel = categoriesSummary(categoryChoices, groups.length)
  const quoteRow: SummaryRow = { key: 'quote', label: 'Devis brouillon', value: `${quote.articleCount} articles · ${formatEuro(quote.solyTotals.totalIncVat)} TVAC`, confirmed: confirmed.has('quote') }
  const categoriesRows: SummaryRow[] = blocks.includes('categories')
    ? [{ key: 'categories', label: 'Catégories', value: categoriesLabel, confirmed: confirmed.has('categories') }]
    : []
  const supplyLabel = supplySummary(supplyChoices, preview.supply?.catalogs.length ?? 0)
  if (blocks.includes('supply'))
    categoriesRows.push({ key: 'supply', label: 'Chez qui commander', value: supplyLabel, confirmed: confirmed.has('supply') })

  const rows: SummaryRow[] = entry
    ? [
        { key: 'project', label: 'Dans le chantier', value: `${entry.name ?? entry.reference ?? ''} · ${entry.customerName ?? ''}`, confirmed: true },
        ...categoriesRows,
        quoteRow,
      ]
    : [
        { key: 'customer', label: 'Client', value: customerLabel, confirmed: confirmed.has('customer') },
        { key: 'project', label: 'Chantier', value: projectLabel, confirmed: confirmed.has('project') },
        ...categoriesRows,
        quoteRow,
      ]

  const index = (block: ReviewBlock) => blocks.indexOf(block) + 1
  const projectReady = project.mode === 'existing' ? !!project.projectId : project.name.trim().length > 0 && !!project.projectTypeId

  return (
    <div className="mx-auto grid max-w-6xl gap-6 py-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="space-y-4">
        {preview.alreadyImported && (
          <Notice testId="winner-import-already-imported">
            Ce devis Winner a déjà été importé
            {preview.alreadyImported.importedAt && <> le {formatDate(preview.alreadyImported.importedAt)}</>}
            {preview.alreadyImported.projectName && <> dans « {preview.alreadyImported.projectName} »</>}. Continuer en crée une nouvelle copie.
          </Notice>
        )}
        {preview.notices.map((notice) => (
          <Notice key={notice} testId="winner-import-notice">{notice}</Notice>
        ))}
        {entry?.customerDiffers && (
          <Notice testId="winner-import-customer-differs">
            Winner parle de « {preview.customer?.displayName} » alors que ce chantier appartient à « {entry.customerName} ». Vérifiez que c&apos;est le bon fichier.
          </Notice>
        )}

        {blocks.includes('customer') && preview.customer && (
          <ReviewBlockCard
            testId="winner-import-block-customer"
            index={index('customer')}
            title="Client"
            summary={customerLabel}
            confirmed={confirmed.has('customer')}
            open={openBlock === 'customer'}
            confirmLabel="C'est le bon client"
            confirmDisabled={customer.mode === 'new' ? customer.name.trim().length === 0 : !customer.customerId}
            onConfirm={() => props.onConfirm('customer')}
            onEdit={() => props.onEdit('customer')}
          >
            <WinnerCustomerBlock winner={preview.customer} candidates={preview.customerCandidates} decision={customer} onChange={props.onCustomerChange} />
          </ReviewBlockCard>
        )}

        {blocks.includes('project') && (
          <ReviewBlockCard
            testId="winner-import-block-project"
            index={index('project')}
            title="Chantier"
            summary={projectLabel}
            confirmed={confirmed.has('project')}
            open={openBlock === 'project'}
            confirmLabel="C'est le bon chantier"
            confirmDisabled={!projectReady}
            onConfirm={() => props.onConfirm('project')}
            onEdit={() => props.onEdit('project')}
          >
            <WinnerProjectBlock
              preview={preview}
              customerId={customer.mode === 'existing' ? customer.customerId ?? null : null}
              decision={project}
              onChange={props.onProjectChange}
            />
          </ReviewBlockCard>
        )}

        {blocks.includes('categories') && preview.categories && (
          <ReviewBlockCard
            testId="winner-import-block-categories"
            index={index('categories')}
            title="Catégories"
            summary={categoriesLabel}
            confirmed={confirmed.has('categories')}
            open={openBlock === 'categories'}
            confirmLabel="Les catégories sont bonnes"
            confirmDisabled={!Object.values(categoryChoices).every(isChoiceComplete)}
            onConfirm={() => props.onConfirm('categories')}
            onEdit={() => props.onEdit('categories')}
          >
            <WinnerCategoriesBlock categories={preview.categories} choices={categoryChoices} onChange={props.onCategoryChange} />
          </ReviewBlockCard>
        )}

        {blocks.includes('supply') && preview.supply && (
          <ReviewBlockCard
            testId="winner-import-block-supply"
            index={index('supply')}
            title={`Chez qui commander · ${preview.supply.catalogs.length} catalogue${preview.supply.catalogs.length > 1 ? 's' : ''}`}
            summary={supplyLabel}
            confirmed={confirmed.has('supply')}
            open={openBlock === 'supply'}
            confirmLabel="C'est bon pour les commandes"
            confirmDisabled={!Object.values(supplyChoices).every(isSupplyChoiceComplete)}
            onConfirm={() => props.onConfirm('supply')}
            onEdit={() => props.onEdit('supply')}
          >
            <WinnerSupplyBlock supply={preview.supply} choices={supplyChoices} onChange={props.onSupplyChange} />
          </ReviewBlockCard>
        )}

        <ReviewBlockCard
          testId="winner-import-block-quote"
          index={index('quote')}
          title={`Devis · ${quote.name}`}
          summary={`${quote.articleCount} articles · ${quote.suppliers.length} fournisseurs · ${formatEuro(quote.solyTotals.totalIncVat)} TVAC`}
          confirmed={confirmed.has('quote')}
          open={openBlock === 'quote'}
          confirmLabel="Les lignes sont bonnes"
          confirmDisabled={!quote.isBalanced}
          onConfirm={() => props.onConfirm('quote')}
          onEdit={() => props.onEdit('quote')}
        >
          <WinnerQuoteBlock quote={quote} />
        </ReviewBlockCard>
      </div>

      <WinnerReviewSummary
        rows={rows}
        remaining={blocks.filter((b) => !confirmed.has(b)).length}
        busy={busy}
        onCreate={props.onCreate}
        createLabel={entry ? 'Ajouter le devis' : 'Créer le dossier'}
      />
    </div>
  )
}

function Notice({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <div data-testid={testId} className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}
